import { ApiError, errorHandler } from "../APIs/errorHandler";
import type { EnquiriesSortOption, FilterOptions } from "../types/enquiries";
import {
  DEFAULT_USER_PREFERENCES,
  type EvoFeedSubscriptionKey,
  type EvoFeedSubscriptions,
  type FilterPreset,
  isRelativeDateRange,
  type RelativeDateRange,
  resolveRelativeDateRange,
  type UserPreferences
} from "../types/userPreferences";

export type PresetSnapshot = {
  filters: FilterOptions;
  sort?: EnquiriesSortOption;
  query?: string;
  /**
   * Store the date range as a token resolved when the preset is applied,
   * rather than freezing the dates it happens to hold today.
   */
  relativeDateRange?: RelativeDateRange;
};

export type PresetUpdates = {
  name: string;
  filters: FilterOptions;
  sort?: EnquiriesSortOption;
  query?: string;
  pinned?: boolean;
  order?: number;
  /** Undefined clears the token and returns the preset to fixed dates. */
  relative_date_range?: RelativeDateRange;
};

/**
 * Presets written by the short-lived seeding of legacy's rail shortcuts.
 *
 * That seeding materialised Confirmed bookings / Today / Next 3 days into
 * every user's saved presets. It has been removed — those three are
 * navigation rendered from a constant in the rail — but the records it
 * already wrote are still on the server, so the rail listed each of them
 * twice: once as a fixed shortcut and once as the operator's "own" preset,
 * complete with edit and delete controls it should never have had.
 *
 * Matching on the id is exact and cannot catch a real preset: a user-created
 * one is `preset-${crypto.randomUUID()}`, so these three literals are
 * unreachable by any other route.
 *
 * Stripped on read rather than written back. A cleanup write would mean every
 * user silently saving on first load, which is the same class of thing that
 * caused this — and the read filter is idempotent, so the rows can sit
 * harmlessly in storage until something else touches them.
 */
export const ORPHANED_SEED_PRESET_IDS: ReadonlySet<string> = new Set([
  "preset-legacy-confirmed-bookings",
  "preset-legacy-today",
  "preset-legacy-next-3-days"
]);

/**
 * Report a preferences load failure. A 401 after the API layer's own refresh
 * retry is already recorded as a telemetry warning — flagging the load error
 * keeps defaults rendered (and save disabled, so defaults can't overwrite
 * saved preferences) without a second, user-facing error report.
 */
export const reportPreferencesLoadError = (error: unknown): void => {
  if (error instanceof ApiError && error.status === 401) {
    return;
  }
  // Fire-and-forget: load state is already set and must not wait on reporting.
  void errorHandler(error, "Failed to load user preferences");
};

export const mergeWithDefaults = (prefs: UserPreferences): UserPreferences => ({
  ...DEFAULT_USER_PREFERENCES,
  ...prefs,
  evo_feed_subscriptions: {
    ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
    ...prefs.evo_feed_subscriptions
  },
  enquiry_filter_presets: (prefs.enquiry_filter_presets ?? []).filter(
    (preset) => !ORPHANED_SEED_PRESET_IDS.has(preset.id)
  ),
  enquiry_pinned_notifications: prefs.enquiry_pinned_notifications ?? []
});

/**
 * Expands a stored preset into the filters to apply right now.
 *
 * A preset with no `relative_date_range` is returned verbatim — that is every
 * preset saved before tokens existed, and any range the user deliberately
 * fixed. A preset with one has its date bounds recomputed from the local
 * clock, so "Next 3 days" means the next three days on every day it is used.
 *
 * An unrecognised token (a newer client wrote it, or the value was corrupted)
 * falls back to the stored dates rather than throwing: this is server-held
 * user data and must survive being read by an older build.
 */
export const resolvePresetSnapshot = (
  preset: FilterPreset,
  now: Date = new Date()
): PresetSnapshot => {
  const base: PresetSnapshot = {
    filters: preset.filters,
    sort: preset.sort,
    query: preset.query
  };

  if (!isRelativeDateRange(preset.relative_date_range)) return base;

  return {
    ...base,
    relativeDateRange: preset.relative_date_range,
    filters: {
      ...preset.filters,
      ...resolveRelativeDateRange(preset.relative_date_range, now)
    }
  };
};

export const applyPreset = (
  preferences: UserPreferences,
  id: string,
  now?: Date
): PresetSnapshot | null => {
  const preset = preferences.enquiry_filter_presets?.find(
    (item) => item.id === id
  );
  return preset ? resolvePresetSnapshot(preset, now) : null;
};

export const withSubscription =
  (key: EvoFeedSubscriptionKey, value: boolean) =>
  (prev: UserPreferences): UserPreferences => {
    const updatedSubscriptions: EvoFeedSubscriptions = {
      ...prev.evo_feed_subscriptions,
      [key]: value
    };

    // If subscribing to all auto-confirmed bookings,
    // force same-day auto-confirmed bookings to true as well
    if (key === "new_autoconfirmed_bookings" && value) {
      updatedSubscriptions.new_same_day_autoconfirmed_bookings = true;
    }

    return { ...prev, evo_feed_subscriptions: updatedSubscriptions };
  };

export const withLocale =
  (locale: string) =>
  (prev: UserPreferences): UserPreferences => ({ ...prev, locale });

export const withLanguage =
  (language: string) =>
  (prev: UserPreferences): UserPreferences => ({ ...prev, language });

export const withPresetAdded =
  (preset: FilterPreset) =>
  (prev: UserPreferences): UserPreferences => ({
    ...prev,
    enquiry_filter_presets: [...(prev.enquiry_filter_presets ?? []), preset]
  });

export const withPresetDeleted =
  (id: string) =>
  (prev: UserPreferences): UserPreferences => ({
    ...prev,
    enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).filter(
      (preset) => preset.id !== id
    )
  });

export const withPresetRenamed =
  (id: string, name: string) =>
  (prev: UserPreferences): UserPreferences => ({
    ...prev,
    enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map((preset) =>
      preset.id === id ? { ...preset, name } : preset
    )
  });

export const withPresetUpdated =
  (id: string, updates: PresetUpdates) =>
  (prev: UserPreferences): UserPreferences => ({
    ...prev,
    enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map((preset) =>
      preset.id === id ? { ...preset, ...updates } : preset
    )
  });

export const withPresetPinned =
  (id: string, pinned: boolean) =>
  (prev: UserPreferences): UserPreferences => ({
    ...prev,
    enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map((preset) =>
      preset.id === id ? { ...preset, pinned } : preset
    )
  });

export const withPresetsReordered =
  (orderedIds: string[]) =>
  (prev: UserPreferences): UserPreferences => ({
    ...prev,
    enquiry_filter_presets: (prev.enquiry_filter_presets ?? []).map(
      (preset) => {
        const index = orderedIds.indexOf(preset.id);
        return index === -1 ? preset : { ...preset, order: index };
      }
    )
  });

export const withPinnedNotificationToggled =
  (value: string) =>
  (prev: UserPreferences): UserPreferences => {
    const current = prev.enquiry_pinned_notifications ?? [];
    return {
      ...prev,
      enquiry_pinned_notifications: current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    };
  };

export const withPinnedNotificationsMerged =
  (values: string[]) =>
  (prev: UserPreferences): UserPreferences => {
    const current = prev.enquiry_pinned_notifications ?? [];
    return {
      ...prev,
      enquiry_pinned_notifications: Array.from(new Set(current.concat(values)))
    };
  };
